import { useState, useEffect } from "react";
import $api from "@/zapi-admin/shared/api/axios";
import { toast } from "sonner";
import { SimpleMarkdownEditor } from "@/zapi-admin/shared/components/SimpleMarkdownEditor/SimpleMarkdownEditor";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/shadcn";
import { FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";
import { PencilSimple, Trash, Plus } from "@phosphor-icons/react";

type Claim = {
  claimmd: string;
  source?: { name: string; id: string } | null;
  sourceType?: "document" | "url" | null;
};

type Benchmark = {
  id: number;
  name: string;
  expectedClaimsCount: number;
  createdAt: string;
  chapterContentPreview: string;
};

type BenchmarkDetail = {
  id: number;
  name: string;
  chapterContent: string;
  expectedClaims: Claim[];
  createdAt: string;
};

interface BenchmarkManagementProps {
  onClose: () => void;
}

export function BenchmarkManagement({ onClose }: BenchmarkManagementProps) {
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBenchmark, setEditingBenchmark] = useState<BenchmarkDetail | null>(null);
  const [editName, setEditName] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editExpectedClaims, setEditExpectedClaims] = useState<Claim[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedEditClaimIndex, setSelectedEditClaimIndex] = useState<number | null>(null);

  const [editingClaimIndex, setEditingClaimIndex] = useState<number | null>(null);
  const [editingClaimText, setEditingClaimText] = useState("");
  const [isAddingClaim, setIsAddingClaim] = useState(false);
  const [newClaimText, setNewClaimText] = useState("");

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  console.log(editExpectedClaims);

  useEffect(() => {
    fetchBenchmarks();
  }, []);

  const fetchBenchmarks = async () => {
    try {
      setIsLoading(true);
      const response = await $api.get("/playground/benchmarks");
      if (response.data.success) {
        setBenchmarks(response.data.benchmarks);
      }
    } catch (error) {
      console.error("Error fetching benchmarks:", error);
      toast.error("Failed to load benchmarks");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (benchmarkId: number) => {
    try {
      const response = await $api.get(`/playground/benchmarks/${benchmarkId}`);
      if (response.data.success) {
        const benchmark = response.data.benchmark;
        // debugger;
        setEditingBenchmark(benchmark);
        setEditName(benchmark.name);
        setEditContent(benchmark.chapterContent);
        setEditExpectedClaims(benchmark.expectedClaims);
        setSelectedEditClaimIndex(null);
        setIsEditModalOpen(true);
      } else {
        toast.error("Failed to load benchmark");
      }
    } catch (error) {
      console.error("Error loading benchmark:", error);
      toast.error("Failed to load benchmark");
    }
  };

  const handleSelectEditClaim = (idx: number) => {
    setSelectedEditClaimIndex(selectedEditClaimIndex === idx ? null : idx);
  };

  const handleAddClaim = () => {
    if (!newClaimText.trim()) {
      toast.error("Please enter claim markdown");
      return;
    }

    const newClaim: Claim = {
      claimmd: newClaimText.trim(),
      source: null,
      sourceType: null
    };

    setEditExpectedClaims([...editExpectedClaims, newClaim]);
    setNewClaimText("");
    setIsAddingClaim(false);
    toast.success("Claim added");
  };

  const handleStartEditClaim = (idx: number) => {
    setEditingClaimIndex(idx);
    setEditingClaimText(editExpectedClaims[idx].claimmd);
    setSelectedEditClaimIndex(null);
  };

  const handleSaveClaimEdit = () => {
    if (editingClaimIndex === null) return;
    if (!editingClaimText.trim()) {
      toast.error("Claim markdown cannot be empty");
      return;
    }

    const updatedClaims = [...editExpectedClaims];
    updatedClaims[editingClaimIndex] = {
      ...updatedClaims[editingClaimIndex],
      claimmd: editingClaimText.trim()
    };

    setSelectedEditClaimIndex(null);
    setEditExpectedClaims(updatedClaims);
    setEditingClaimIndex(null);
    setEditingClaimText("");
    toast.success("Claim updated");
  };

  const handleCancelClaimEdit = () => {
    setEditingClaimIndex(null);
    setEditingClaimText("");
  };

  const handleDeleteClaim = (idx: number) => {
    const updatedClaims = editExpectedClaims.filter((_, i) => i !== idx);
    setEditExpectedClaims(updatedClaims);
    setSelectedEditClaimIndex(null);
    toast.success("Claim deleted");
  };

  const handleGenerateExpectedClaims = async () => {
    if (!editContent.trim()) {
      toast.error("Please enter benchmark content first");
      return;
    }

    if (!editingBenchmark) {
      toast.error("No benchmark selected");
      return;
    }

    setIsGenerating(true);

    try {
      const response = await $api.post("/playground/test-claims-extraction", {
        chapterContent: editContent,
        benchmarkId: editingBenchmark.id
      });

      if (response.data.success) {
        setEditExpectedClaims(response.data.claims);
        setSelectedEditClaimIndex(null);
        toast.success(`Generated ${response.data.claims.length} expected claims`);
      } else {
        toast.error("Failed to generate claims");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to generate claims");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingBenchmark) return;

    if (!editName.trim()) {
      toast.error("Please enter a benchmark name");
      return;
    }

    if (!editContent.trim()) {
      toast.error("Please enter benchmark content");
      return;
    }

    if (editExpectedClaims.length === 0) {
      toast.error("Please generate expected claims first");
      return;
    }

    setIsSaving(true);

    try {
      const response = await $api.put(`/playground/benchmarks/${editingBenchmark.id}`, {
        name: editName,
        chapterContent: editContent,
        expectedClaims: editExpectedClaims
      });

      if (response.data.success) {
        toast.success("Benchmark updated successfully");
        setIsEditModalOpen(false);
        setEditingBenchmark(null);
        await fetchBenchmarks();
      } else {
        toast.error("Failed to update benchmark");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to update benchmark");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (benchmarkId: number) => {
    setIsDeleting(true);

    try {
      const response = await $api.delete(`/playground/benchmarks/${benchmarkId}`);

      if (response.data.success) {
        toast.success("Benchmark deleted successfully");
        setDeleteConfirmId(null);
        await fetchBenchmarks();
      } else {
        toast.error("Failed to delete benchmark");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to delete benchmark");
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  return (
    <>
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4 pb-4 border-b">
          <FigmaTypography component="h2" variant="memo-heading-2">Manage Benchmarks</FigmaTypography>
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
            ← Back to Tests
          </button>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <p>Loading benchmarks...</p>
          </div>
        ) : benchmarks.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
            <p className="text-lg mb-2">No benchmarks yet</p>
            <p className="text-[14px]">Create a benchmark to get started</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-3">
              {benchmarks.map((benchmark) => (
                <div
                  key={benchmark.id}
                  className="p-4 rounded-lg border bg-white border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{benchmark.name}</h3>
                      <p className="text-[14px] text-gray-600 mb-2 line-clamp-2">{benchmark.chapterContentPreview}</p>
                      <div className="flex gap-3 text-[12px] leading-[18px] text-gray-500">
                        <span>{benchmark.expectedClaimsCount} expected claims</span>
                        <span>•</span>
                        <span>Created {formatDate(benchmark.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4">
                      <button
                        onClick={() => handleEdit(benchmark.id)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit benchmark"
                      >
                        <PencilSimple size={18} weight="regular" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(benchmark.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete benchmark"
                      >
                        <Trash size={18} weight="regular" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={isEditModalOpen}
        onOpenChange={(open) => {
          setIsEditModalOpen(open);
          if (!open) {
            setSelectedEditClaimIndex(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Benchmark</DialogTitle>
            <DialogDescription>Update the benchmark details and expected claims.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1">
            <div>
              <label className="block text-[14px] font-medium text-gray-700 mb-1">Benchmark Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="e.g., Standard Business Chapter Test"
                disabled={isGenerating || isSaving}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-[14px] font-medium text-gray-700 mb-1">Chapter Content</label>
              <SimpleMarkdownEditor
                content={editContent}
                onChange={setEditContent}
                placeholder="Enter the markdown content for this benchmark..."
                highlightText={
                  selectedEditClaimIndex !== null && editExpectedClaims[selectedEditClaimIndex]
                    ? editExpectedClaims[selectedEditClaimIndex].claimmd
                    : undefined
                }
                readOnly={isGenerating || isSaving}
              />
              <p className="text-[12px] leading-[18px] text-gray-500 mt-1">
                {editContent.length} characters
                {(isGenerating || isSaving) && (
                  <span className="text-orange-600 ml-2">
                    (Read-only while {isGenerating ? "generating claims" : "saving"})
                  </span>
                )}
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <label className="block text-[14px] font-medium text-gray-700">Expected Claims</label>
                </div>
                <button
                  onClick={handleGenerateExpectedClaims}
                  disabled={isGenerating || !editContent.trim()}
                  className="px-3 py-1.5 text-[14px] bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isGenerating ? "Generating..." : "Regenerate Claims"}
                </button>
              </div>

              {isAddingClaim && (
                <div className="mb-3 p-3 border border-green-300 rounded-lg bg-green-50">
                  <label className="block text-[14px] font-medium text-gray-700 mb-1">New Claim</label>
                  <input
                    type="text"
                    value={newClaimText}
                    onChange={(e) => setNewClaimText(e.target.value)}
                    placeholder="Enter claim text..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-green-500 mb-2"
                    onKeyDown={(e) => {
                      if (isEnterSubmit(e, { allowShift: true })) handleAddClaim();
                      if (e.key === "Escape") {
                        setIsAddingClaim(false);
                        setNewClaimText("");
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddClaim}
                      className="px-3 py-1 text-[14px] bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setIsAddingClaim(false);
                        setNewClaimText("");
                      }}
                      className="px-3 py-1 text-[14px] bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {editExpectedClaims.length > 0 ? (
                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex justify-between items-center">
                    <p className="text-[14px] text-gray-600 mb-2">
                      {editExpectedClaims.length} claims. Click on any claim to highlight it in the content above.
                    </p>
                    <button
                      onClick={() => setIsAddingClaim(true)}
                      disabled={isGenerating || isSaving}
                      className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Add claim"
                    >
                      <Plus size={18} weight="bold" />
                    </button>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto space-y-1">
                    {editExpectedClaims.map((claim, idx) => (
                      <div key={idx}>
                        {editingClaimIndex === idx ? (
                          <div className="p-3 border border-blue-300 rounded bg-blue-50">
                            <label className="block text-[12px] leading-[18px] font-medium text-gray-700 mb-1">
                              Edit Claim Markdown (this exact text will be matched in the content)
                            </label>
                            <textarea
                              value={editingClaimText}
                              onChange={(e) => setEditingClaimText(e.target.value)}
                              className="w-full px-3 py-2 text-[14px] border border-gray-300 rounded focus:outline-hidden focus:border-blue-500 mb-2 font-mono min-h-[80px]"
                              onKeyDown={(e) => {
                                if (e.key === "Escape") handleCancelClaimEdit();
                              }}
                              placeholder="Enter the exact markdown text to match..."
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={handleSaveClaimEdit}
                                className="px-3 py-1.5 text-[12px] leading-[18px] bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelClaimEdit}
                                className="px-3 py-1.5 text-[12px] leading-[18px] bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleSelectEditClaim(idx)}
                              className={`flex-1 text-left text-[12px] leading-[18px] p-2 rounded border transition-colors ${
                                selectedEditClaimIndex === idx
                                  ? "bg-blue-50 border-blue-300"
                                  : "bg-white border-gray-200 hover:bg-gray-50"
                              }`}
                            >
                              {claim.claimmd}
                            </button>
                            <button
                              onClick={() => handleStartEditClaim(idx)}
                              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit claim"
                            >
                              <PencilSimple size={16} weight="regular" />
                            </button>
                            <button
                              onClick={() => handleDeleteClaim(idx)}
                              className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete claim"
                            >
                              <Trash size={16} weight="regular" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[14px] text-gray-500">
                  Click "Regenerate Claims" to generate claims from the content, or the + icon to add manually.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={isSaving || !editName.trim() || !editContent.trim() || editExpectedClaims.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmId !== null} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Benchmark</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this benchmark? This action cannot be undone. All tests associated with
              this benchmark will remain but will no longer reference it.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <button
              onClick={() => setDeleteConfirmId(null)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isDeleting ? "Deleting..." : "Delete Benchmark"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
